<template>
  <section class="category-seo-content container p15">
    <breadcrumbs class="pt20 pb20" :current-category="category" :current-tag="tag" />

    <div class="intro-card mb30">
      <h2>{{ introHeading }}</h2>
      <p>{{ introParagraph }}</p>
    </div>

    <div class="faq-block mb30" v-if="faqItems.length">
      <h2 class="section-title">Часті запитання</h2>
      <div class="faq-card">
        <div
          class="faq-item"
          v-for="(item, index) in faqItems"
          :key="index"
          :class="{ last: index === faqItems.length - 1 }"
        >
          <h3 class="faq-question" @click="toggleFaq(index)">
            <span>{{ item.question }}</span>
            <i class="material-icons arrow-i" :class="{ active: openFaqIndex === index }">keyboard_arrow_right</i>
          </h3>
          <p v-show="openFaqIndex === index">{{ item.answer }}</p>
        </div>
      </div>
      <router-link class="faq-more-link" :to="localizedRoute('/FAQ')">Більше запитань →</router-link>
    </div>

    <div class="cross-links-block mb30" v-if="crossLinks.length">
      <h2 class="section-title">Дивіться також</h2>
      <div class="cross-links-list">
        <router-link
          v-for="link in crossLinks"
          :key="link.code"
          class="cross-link"
          :to="localizedRoute(link.path)"
        >
          {{ link.title }} в {{ locativeLocationName }}
        </router-link>
      </div>
    </div>
  </section>
</template>

<script>
import config from 'config'
import { localizedRoute } from '@vue-storefront/core/lib/multistore'
import { pluralizeUk } from 'theme/helpers/pluralizeUk'
import { declineCityLocative } from 'theme/helpers/declineCityLocative'
import Breadcrumbs from 'theme/components/core/Breadcrumbs.vue'

export default {
  name: 'CategorySeoContent',
  components: {
    Breadcrumbs
  },
  data () {
    return {
      // First question open by default, matching the static /FAQ page's
      // accordion convention (FAQ.vue). Collapsed by default otherwise -
      // this block used to render every answer expanded, which made the
      // listing page (whose job is showing the partner grid) end in a wall
      // of text.
      openFaqIndex: 0
    }
  },
  methods: {
    localizedRoute,
    toggleFaq (index) {
      this.openFaqIndex = this.openFaqIndex === index ? null : index
    },
    // '<' is escaped so embedded partner/category/tag text (e.g. a name
    // containing a literal closing-script-tag sequence) can never break out
    // of the inline JSON-LD script block - the HTML parser looks for that
    // raw byte sequence regardless of the tag's type or JS semantics. Keep
    // that exact sequence out of source comments too, not just data: the
    // vue-loader SFC compiler scans for it the same naive way to find where
    // this <script> block ends.
    toJsonLd (value) {
      return JSON.stringify(value).replace(/</g, '\\u003c')
    },
    // schema.org OpeningHoursSpecification per enabled weekday, sourced from
    // the same partner.schedule.working_schedule shape vuestorefront-api's
    // partnerWorkingTimeHelper.js reads for the "is this partner open now"
    // badge - not the (stale/unused) `regular` field the frontend Schedule
    // type declares.
    openingHoursSpecification (store) {
      const schedule = (store && store.schedule) || {}
      const workingSchedule = Array.isArray(schedule.working_schedule) ? schedule.working_schedule : []
      return workingSchedule
        .filter(day => day.enable && day.from && day.to)
        .map(day => ({
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: `https://schema.org/${day.day.charAt(0).toUpperCase()}${day.day.slice(1)}`,
          opens: day.from,
          closes: day.to
        }))
    }
  },
  props: {
    category: {
      type: Object,
      required: true
    },
    // The cuisine tag (Суші, Піца...) is the real ranking target for local
    // search intent - the partner category ("food"/"shop") is coarser and
    // only used for the URL/breadcrumb structure. Null on the category-only
    // (no tag selected) view.
    tag: {
      type: Object,
      default: null
    },
    location: {
      type: Object,
      required: true
    },
    stats: {
      type: Object,
      default: () => ({ count: 0, avg_delivery_time: null, top_partners: [], loaded: false })
    },
    stores: {
      type: Array,
      default: () => []
    },
    siblingCategories: {
      type: Array,
      default: () => []
    },
    siblingTags: {
      type: Array,
      default: () => []
    }
  },
  computed: {
    // The entity this page's copy is actually about: the tag when one is
    // selected, otherwise the category itself.
    entityTitle () {
      return this.tag ? this.tag.title : this.category.title
    },
    // Locative case for "в {city}" phrasing - see declineCityLocative's own
    // comment for why unmapped cities fall back to their raw name.
    locativeLocationName () {
      return declineCityLocative(this.location.name)
    },
    entityPath () {
      return this.tag
        ? `/${this.location.code}/${this.category.code}/${this.tag.code}`
        : `/${this.location.code}/${this.category.code}`
    },
    // Ranks siblings by how many of THIS page's partners also serve them - a
    // real relevance signal (no new API calls: partner.categories/.tags are
    // already fetched with the page's own store list), replacing the old
    // "first 5 in whatever order the store returns" placeholder. Ties keep
    // their original (alphabetical, from getCategories/getTags) order since
    // Array#sort is stable.
    crossLinks () {
      const siblings = this.tag ? this.siblingTags : this.siblingCategories
      const field = this.tag ? 'tags' : 'categories'
      const overlapCount = sibling => this.stores.filter(store =>
        Array.isArray(store[field]) && store[field].includes(sibling.entity_id)
      ).length
      const ranked = [...siblings].sort((a, b) => overlapCount(b) - overlapCount(a)).slice(0, 5)

      if (this.tag) {
        return ranked.map(sibling => ({
          code: sibling.code,
          title: sibling.title,
          path: `/${this.location.code}/${this.category.code}/${sibling.code}`
        }))
      }
      return ranked.map(sibling => ({
        code: sibling.code,
        title: sibling.title,
        path: `/${this.location.code}/${sibling.code}`
      }))
    },
    partnerCount () {
      return this.stats.count || 0
    },
    avgDeliveryTime () {
      return this.stats.avg_delivery_time
    },
    topPartnerNames () {
      return (this.stats.top_partners || []).map(p => p.title)
    },
    // Only true once stats have actually resolved to zero - never while
    // they're simply unloaded, so a stats-endpoint hiccup doesn't make a
    // real page claim it has no venues.
    isConfirmedEmpty () {
      return !!this.stats.loaded && this.partnerCount === 0
    },
    // Copy below follows the exact templates from K2-2985 (avg_rating clause
    // dropped - no partner rating data exists anywhere in the system yet).
    introHeading () {
      if (this.isConfirmedEmpty) {
        return `${this.entityTitle} в ${this.locativeLocationName}`
      }
      const venues = pluralizeUk(this.partnerCount, ['заклад', 'заклади', 'закладів'])
      return `${this.entityTitle} в ${this.locativeLocationName}: ${this.partnerCount} ${venues} на вибір`
    },
    introParagraph () {
      if (this.isConfirmedEmpty) {
        return `Наразі в ${this.locativeLocationName} немає доступних закладів у категорії «${this.entityTitle}». ` +
          `Спробуйте пізніше або оберіть іншу категорію нижче.`
      }
      const first = this.topPartnerNames[0]
      const second = this.topPartnerNames[1]
      const rangeText = first && second ? ` — від ${first} до ${second}` : ''
      const deliveryText = this.avgDeliveryTime ? ` Середній час доставки — ${this.avgDeliveryTime} хв.` : ''

      return `Оберіть ${this.entityTitle} з ${this.partnerCount}+ закладів у ${this.locativeLocationName}${rangeText}.` +
        `${deliveryText}` +
        ` Замовляйте онлайн, обирайте зручний спосіб оплати та відстежуйте замовлення в реальному часі.`
    },
    deliveryFeeText () {
      const price = this.location && this.location.base_delivery_price
      return price ? `від ${price} грн` : 'залежить від відстані до закладу'
    },
    faqItems () {
      if (this.isConfirmedEmpty) {
        return []
      }
      const items = []
      items.push({
        question: `Скільки коштує доставка ${this.entityTitle} в ${this.locativeLocationName}?`,
        answer: `Вартість доставки ${this.deliveryFeeText}.`
      })
      if (this.avgDeliveryTime) {
        items.push({
          question: `За скільки часу доставляють ${this.entityTitle} в ${this.locativeLocationName}?`,
          answer: `У середньому за ${this.avgDeliveryTime} хв.`
        })
      }
      if (this.topPartnerNames.length) {
        items.push({
          question: `Які заклади ${this.entityTitle} є в ${this.locativeLocationName}?`,
          answer: this.topPartnerNames.slice(0, 5).join(', ') + '.'
        })
      }
      return items
    },
    breadcrumbListSchema () {
      const itemListElement = [
        { '@type': 'ListItem', position: 1, name: 'Korzyna', item: this.hostPath + '/' },
        { '@type': 'ListItem', position: 2, name: this.location.name, item: `${this.hostPath}/${this.location.code}` }
      ]
      if (this.tag) {
        itemListElement.push(
          { '@type': 'ListItem', position: 3, name: this.category.title, item: `${this.hostPath}/${this.location.code}/${this.category.code}` },
          { '@type': 'ListItem', position: 4, name: this.tag.title, item: `${this.hostPath}${this.entityPath}` }
        )
      } else {
        itemListElement.push(
          { '@type': 'ListItem', position: 3, name: this.category.title, item: `${this.hostPath}${this.entityPath}` }
        )
      }
      return { '@context': 'https://schema.org', '@type': 'BreadcrumbList', itemListElement }
    },
    itemListSchema () {
      return {
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        itemListElement: this.stores.map((store, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          item: {
            // display_mode is the only reliable type signal on a partner (used
            // the same way for layout switching elsewhere, e.g. PartnerView.vue) -
            // not every partner is a restaurant (bars/cafes/shops all list here
            // too), so FoodEstablishment/Store is as specific as this data
            // honestly supports without guessing at a finer schema.org type.
            '@type': store.display_mode === 'shop' ? 'Store' : 'FoodEstablishment',
            name: store.title,
            url: `${this.hostPath}/${this.location.code}/${store.code}`,
            ...(store.address ? { address: store.address } : {}),
            ...(this.openingHoursSpecification(store).length ? { openingHoursSpecification: this.openingHoursSpecification(store) } : {})
          }
        }))
      }
    },
    faqPageSchema () {
      return {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: this.faqItems.map(item => ({
          '@type': 'Question',
          name: item.question,
          acceptedAnswer: { '@type': 'Answer', text: item.answer }
        }))
      }
    },
    hostPath () {
      return config.canonical ? config.canonical.url : ''
    }
  },
  metaInfo () {
    return {
      script: [
        { type: 'application/ld+json', innerHTML: this.toJsonLd(this.breadcrumbListSchema), vmid: 'ld-breadcrumb' },
        { type: 'application/ld+json', innerHTML: this.toJsonLd(this.itemListSchema), vmid: 'ld-itemlist' },
        { type: 'application/ld+json', innerHTML: this.toJsonLd(this.faqPageSchema), vmid: 'ld-faq' }
      ],
      // Without this, vue-meta HTML-entity-escapes innerHTML by default
      // (its own documented behavior) - the JSON-LD above would render as
      // {&quot;@context&quot;:...} instead of {"@context":...}, which is not
      // valid JSON to any parser reading the raw script text (browsers don't
      // decode entities inside <script>, per the HTML spec's raw-text-element
      // parsing rules). toJsonLd() already escapes '<' itself for the actual
      // XSS/breakout risk this flag would otherwise guard against.
      __dangerouslyDisableSanitizers: ['script']
    }
  }
}
</script>

<style lang="scss" scoped>
.category-seo-content {
  // Matches the site's existing card language (Product.vue's .info-store
  // delivery-info blocks: soft grey bg, 10px radius) instead of the plain
  // black-on-white text this block used to be.
  // Mobile sizes are the base (mobile is the primary target - most of this
  // site's traffic is phones), scaled up for larger viewports at the same
  // 768px breakpoint the rest of the theme uses (Product.vue etc.).
  .section-title {
    font-size: 16px;
    font-weight: 700;
    color: #111;
    margin: 0 0 12px;

    @media only screen and (min-width: 769px) {
      font-size: 18px;
      margin-bottom: 14px;
    }
  }

  .intro-card {
    background-color: #f9f8f6;
    border-radius: 10px;
    padding: 16px;

    @media only screen and (min-width: 769px) {
      padding: 24px;
    }

    h2 {
      font-size: 18px;
      margin: 0 0 8px;
      color: #111;
      line-height: 1.3;

      @media only screen and (min-width: 769px) {
        font-size: 22px;
        margin-bottom: 10px;
      }
    }
    p {
      margin: 0;
      color: #666;
      line-height: 1.5;
      font-size: 14px;

      @media only screen and (min-width: 769px) {
        font-size: 16px;
      }
    }
  }

  .faq-card {
    border: 1px solid #ebebeb;
    border-radius: 10px;
    padding: 0 16px;

    @media only screen and (min-width: 769px) {
      padding: 0 20px;
    }
  }

  .faq-item {
    padding: 14px 0;
    border-bottom: 1px solid #ebebeb;

    @media only screen and (min-width: 769px) {
      padding: 16px 0;
    }

    &.last {
      border-bottom: none;
    }

    h3.faq-question {
      cursor: pointer;
      font-size: 14px;
      margin: 0;
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;

      @media only screen and (min-width: 769px) {
        font-size: 16px;
      }

      .arrow-i {
        flex-shrink: 0;
        background-color: #f5f5f5;
        padding: 5px;
        border-radius: 50%;
        transform: rotate(90deg);
        color: #11bb2e;
        transition: transform .2s;

        &.active {
          transform: rotate(-90deg);
        }
      }
    }
    p {
      margin: 10px 0 0;
      color: #666;
      line-height: 1.5;
      font-size: 14px;
    }
  }

  .faq-more-link {
    display: inline-block;
    margin-top: 16px;
    color: #11bb2e;
    text-decoration: underline;
    font-weight: 700;
    font-size: 14px;
  }

  .cross-links-list {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;

    @media only screen and (min-width: 769px) {
      gap: 10px;
    }
  }

  // Pill shape matches PartnerTagChips.vue's .tag-wrap, reused here so the
  // "see also" row visually rhymes with the tag chips right above it on the
  // same page instead of reading as unrelated plain text links. Height kept
  // at 35px on every breakpoint to match that existing chip size sitewide.
  .cross-link {
    display: inline-flex;
    align-items: center;
    height: 35px;
    padding: 0 14px;
    border-radius: 35px;
    border: 1px solid #ebebeb;
    background-color: #ffffff;
    font-size: 13px;
    color: #111;
    transition: border-color .2s, color .2s;

    @media only screen and (min-width: 769px) {
      padding: 0 20px;
      font-size: 14px;
    }

    &:hover {
      border-color: #11bb2e;
      color: #11bb2e;
    }
  }
}
</style>
